/**
 * Pure builders for the recession + strong-expansion probability chart.
 *
 * The `/api/economic-outlook/recession` pipeline is rendered by
 * `features/economic-outlook/RecessionChart.tsx` (550px detail page).
 * This module owns the shared 7-day rolling-average smoother and the
 * two-trace construction (`*100` scaling, fixed colors and names);
 * `height` is a layout parameter so one builder can serve any size.
 *
 * No React, no feature-state reads, no side effects — safe to unit-test
 * directly.
 */
import type { Data, Layout, Shape } from 'plotly.js';
import { hoverTemplate } from './hoverTemplates';
import { chartLayout } from './layoutPresets';

/**
 * 7-day rolling average. Each output position is the mean of the trailing
 * window (`[i - windowSize + 1, i]`), ignoring null / non-finite entries; a
 * position whose window has no finite values yields `null`.
 *
 * Byte-identical to the two `rollingAverage` copies it replaces.
 */
export function rollingAverage(
  values: (number | null)[],
  windowSize = 7,
): (number | null)[] {
  const result: (number | null)[] = [];
  for (let i = 0; i < values.length; i += 1) {
    const start = Math.max(0, i - windowSize + 1);
    const window = values
      .slice(start, i + 1)
      .filter((v): v is number => v !== null && Number.isFinite(v));
    result.push(window.length > 0 ? window.reduce((s, v) => s + v, 0) / window.length : null);
  }
  return result;
}

/**
 * Result of {@link buildRecessionTraces}: the smoothed x/y series plus the
 * two Plotly traces. `x` and the raw date bounds are returned so the caller
 * can build the `+1 month` padded x-range for the layout.
 */
export interface RecessionTracesResult {
  /** Sorted, de-duplicated observation dates (ascending). */
  x: string[];
  /** The two Plotly traces: Recession then Strong Expansion. */
  traces: Data[];
  /** First date in `x`, or `null` when there are no rows. */
  firstDate: string | null;
  /** Last date in `x`, or `null` when there are no rows. */
  lastDate: string | null;
}

/** Minimal row contract: the recession payload is loosely typed upstream. */
export interface RecessionRowLike {
  obs_date?: string;
  date?: string;
  recessionprobability?: number | null;
  strongrecoveryprobability?: number | null;
  [col: string]: unknown;
}

function rowDate(row: RecessionRowLike): string | null {
  return (row.obs_date ?? row.date) as string | null;
}

/**
 * Sort rows by date, extract `recessionprobability` /
 * `strongrecoveryprobability` (scaled to %), apply the 7-day smoother, and
 * emit the two line traces.
 *
 * Trace names ("Recession" / "Strong Expansion"), colors (`#193947` /
 * `#7ACAC5`), line width (2) and hover templates are fixed — both consumers
 * render them identically.
 */
export function buildRecessionTraces(
  rows: readonly RecessionRowLike[],
): RecessionTracesResult {
  const sorted = [...rows]
    .filter((r) => rowDate(r) !== null)
    .sort((a, b) => (rowDate(a) ?? '').localeCompare(rowDate(b) ?? ''));

  if (sorted.length === 0) {
    return { x: [], traces: [], firstDate: null, lastDate: null };
  }

  const x: string[] = [];
  const yRecessionRaw: (number | null)[] = [];
  const yRecoveryRaw: (number | null)[] = [];
  for (const row of sorted) {
    const d = rowDate(row);
    if (!d) continue;
    x.push(d);
    const r = row.recessionprobability;
    const s = row.strongrecoveryprobability;
    yRecessionRaw.push(typeof r === 'number' && Number.isFinite(r) ? r * 100 : null);
    yRecoveryRaw.push(typeof s === 'number' && Number.isFinite(s) ? s * 100 : null);
  }

  const yRecession = rollingAverage(yRecessionRaw);
  const yRecovery = rollingAverage(yRecoveryRaw);

  const recessionTrace: Data = {
    x,
    y: yRecession,
    name: 'Recession',
    type: 'scatter',
    mode: 'lines',
    line: { color: '#193947', width: 2 },
    hovertemplate: hoverTemplate({ name: 'Recession', format: '.1f', suffix: '%' }),
  };
  const recoveryTrace: Data = {
    x,
    y: yRecovery,
    name: 'Strong Expansion',
    type: 'scatter',
    mode: 'lines',
    line: { color: '#7ACAC5', width: 2 },
    hovertemplate: hoverTemplate({ name: 'Strong Expansion', format: '.1f', suffix: '%' }),
  };

  return {
    x,
    traces: [recessionTrace, recoveryTrace],
    firstDate: x[0] ?? null,
    lastDate: x[x.length - 1] ?? null,
  };
}

/** Options for {@link buildRecessionLayout}. */
export interface RecessionLayoutOptions {
  /** Plotly title text. */
  title: string;
  /** Sorted dates from {@link buildRecessionTraces} (used to size the x-range). */
  x: string[];
  /** Recession-band shapes (caller decides whether to gate by country). */
  shapes: Partial<Shape>[];
  /**
   * Title font size. RecessionChart (detail page) uses 16; the dashboard
   * teaser uses 14.
   */
  titleFontSize: number;
  /**
   * Title `y` position (paper coords). RecessionChart uses 1.04; the
   * dashboard teaser uses 1.14.
   */
  titleY: number;
  /**
   * Margin overrides merged onto `chartLayout`'s defaults.
   * RecessionChart keeps the default (`t:85`); the dashboard teaser passes
   * `{ l:55, r:20, t:70, b:35 }`.
   */
  marginOverride?: Partial<Layout['margin']>;
  /**
   * Whether to add the right-side mirrored `yaxis2`. RecessionChart includes
   * it; the dashboard teaser does not. Default `false`.
   */
  includeYAxis2?: boolean;
  /**
   * Optional explicit chart height. Both current consumers size the chart
   * via a `100%`-height wrapper div and leave `layout.height` unset — pass
   * `undefined` to preserve that. Provide a number to pin the height in the
   * layout itself.
   */
  height?: number;
}

/**
 * Build the recession-chart layout.
 *
 * Composes `chartLayout({ family: 'timeseries' })` and applies the repo-wide
 * "title above legend above plot" stacking that both consumers need (the
 * shared helper puts the title at `y:0.98`, overlapping
 * the top "100%" tick). All the parts that differ between the detail page
 * and the dashboard teaser are passed in via {@link RecessionLayoutOptions}.
 */
export function buildRecessionLayout(opts: RecessionLayoutOptions): Partial<Layout> {
  const {
    title,
    x,
    shapes,
    titleFontSize,
    titleY,
    marginOverride,
    includeYAxis2 = false,
    height,
  } = opts;

  // `+1 month` padding on the right edge of the x-range.
  const lastDate = new Date(x[x.length - 1] ?? '');
  const xRangeEnd = new Date(lastDate);
  xRangeEnd.setMonth(xRangeEnd.getMonth() + 1);

  const conv = chartLayout({
    family: 'timeseries',
    title,
    ...(marginOverride ? { marginOverride } : {}),
  });

  // Stack the title ABOVE the legend ABOVE the plot so neither overlaps the
  // top y-axis tick ("100%").
  const titleAboveLegend =
    conv.title && typeof conv.title === 'object'
      ? {
          ...conv.title,
          font: { ...conv.title.font, size: titleFontSize },
          y: titleY,
          yanchor: 'bottom' as const,
        }
      : conv.title;
  // Legacy recession-probability charts (`recession-probabilities.js`,
  // `nowcasts.js`) render the legend at size 12; the shared preset now
  // defaults to 16, so pin it back to 12 here.
  const legendBelowTitle =
    conv.legend && typeof conv.legend === 'object'
      ? {
          ...conv.legend,
          y: 1.02,
          yanchor: 'bottom' as const,
          font: { ...(conv.legend.font ?? {}), size: 12 },
        }
      : conv.legend;

  const layout: Partial<Layout> = {
    ...conv,
    title: titleAboveLegend,
    legend: legendBelowTitle,
    xaxis: {
      type: 'date',
      range: [x[0], xRangeEnd.toISOString().split('T')[0]],
      showgrid: true,
      gridcolor: '#DCDCDC',
      gridwidth: 1,
      ...conv.xaxis,
    },
    yaxis: {
      ...conv.yaxis,
      title: { text: 'Probability (%)', standoff: 10 },
      range: [0, 100],
      tickformat: '.0f',
      ticksuffix: '%',
      showgrid: true,
      gridcolor: '#DCDCDC',
      gridwidth: 1,
    },
    shapes,
  };

  if (includeYAxis2) {
    layout.yaxis2 = {
      overlaying: 'y',
      side: 'right',
      showgrid: false,
      range: [0, 100],
      tickformat: '.0f',
      showline: true,
      linecolor: '#000000',
      linewidth: 1,
      mirror: true,
      fixedrange: true,
    };
  }

  if (height !== undefined) {
    layout.height = height;
  }

  return layout;
}
